import {
  useWSModalCustom,
  useWSSnackbar,
  WSForm,
  WSInputDate,
  WSInputNumber,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { queriesInvoices, queriesInvoiceRows } from "@query";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import { QUERY_INVOICE } from "../../query/payments/keys";

type FormData = {
  amount: number;
  paymentMethodDescription: string;
  paidDate: Date;
  referenceNumber: string;
  notes: string;
};

// Accepts the minimal fields both the v1 receivables row and the v3 Invoice carry,
// so the modal is shared by the list action and the invoice-detail action.
type Props = {
  invoiceId: string;
  amount: number;
  currency: string;
  invoiceNumber?: string;
} & WSModalCustomProps<void>;

const ModalMarkInvoiceDeposited: React.FC<Props> = props => {
  const { openSnackbar } = useWSSnackbar();
  const [payInvoiceOffPlatform, payInvoiceOffPlatformMeta] =
    queriesInvoices.payInvoiceOffPlatform.useMutation({
      dependencies: [
        queriesInvoiceRows.searchInvoiceRows.queryKeyBase,
        QUERY_INVOICE
      ]
    });

  return (
    <WSForm<FormData>
      defaultValues={{
        amount: props.amount,
        paymentMethodDescription: "",
        paidDate: new Date(),
        referenceNumber: "",
        notes: ""
      }}
      validationSchema={Yup.object().shape({
        paymentMethodDescription: Yup.string().required(
          "Payment method is required"
        ),
        paidDate: Yup.date().required("Payment date is required")
      })}
      onSubmit={async formData => {
        await payInvoiceOffPlatform(
          {
            invoiceId: props.invoiceId,
            offPlatformPaymentRequest: {
              amount: props.amount,
              paymentMethodDescription: formData.paymentMethodDescription,
              paidDate: formData.paidDate,
              referenceNumber: formData.referenceNumber || undefined,
              notes: formData.notes || undefined
            }
          },
          {
            onSuccess: () => {
              openSnackbar({
                type: "success",
                message: `Invoice #${
                  props.invoiceNumber ?? ""
                } marked as deposited`
              });
              props.onClose();
            }
          }
        );
      }}
    >
      <WSModal
        title="Mark invoice as deposited"
        size="S"
        onClose={props.onClose}
        actions={{
          buttons: [
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick: () => props.onClose()
            },
            {
              label: "Mark deposited",
              loading: payInvoiceOffPlatformMeta.isLoading
            }
          ]
        }}
      >
        <WSList gap="M">
          <WSText color="gray500">
            Record a payment you received for this invoice outside of Wingspan.
          </WSText>

          <WSForm.Field
            name="amount"
            label="Amount"
            component={WSInputNumber}
            componentProps={{
              currency: props.currency,
              maxFractionDigits: 2,
              disabled: true
            }}
          />

          <WSForm.Field
            name="paymentMethodDescription"
            label="Payment method"
            component={WSInputText}
            componentProps={{
              placeholder: "e.g. Check, Wire transfer, Cash"
            }}
          />

          <WSForm.Field
            name="paidDate"
            label="Payment date"
            component={WSInputDate}
          />

          <WSForm.Field
            name="referenceNumber"
            label="Reference number (optional)"
            component={WSInputText}
          />

          <WSForm.Field
            name="notes"
            label="Notes (optional)"
            component={WSInputTextarea}
          />

          <WSErrorMessage
            contextKey="Other"
            error={payInvoiceOffPlatformMeta.error}
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalMarkInvoiceDeposited = () =>
  useWSModalCustom(ModalMarkInvoiceDeposited);
